import React, { CSSProperties, FC } from "react";
import { Tooltip, makeStyles } from "@material-ui/core";
import {
	CALENDAR_ENTRY_HEIGHT,
	CALENDAR_ENTRY_TYPE_HEIGHT,
	CalendarEntry,
} from "Components/Calendar/CalendarEntryHelper";
import { px } from "Configs/theme";
import {
	getColorRgbaFromHex,
	getTextColorFromBackgroundColor,
} from "Helpers/ColorHelper";
import { DragElementWrapper, DragPreviewOptions } from "react-dnd";

const MERGED_BACKGROUND_LINE_WIDTH = 5;

interface Props {
	calendarEntry: CalendarEntry;
	colorHex: string;
	isDragging: boolean;
	isResizing?: boolean;
	isCutOff: {
		left: boolean;
		right: boolean;
	};
	style: CSSProperties;
	preview: DragElementWrapper<DragPreviewOptions>;
	onClick: () => void;
	colorHexes?: string[];
	isSingleRow?: boolean;
	tooltip: JSX.Element | null;
}

interface StyleProps {
	colorHex: string;
	isScheduleEntryType: boolean;
	isSingleRow: boolean;
	isDragging: boolean;
	backgroundImage?: string;
	hoverBackgroundImage?: string;
}

const CalendarEntryDraggableView: FC<Props> = (props) => {
	const isScheduleEntryType = !!props.calendarEntry.typeId;
	const styleProps: StyleProps = {
		colorHex: props.colorHex,
		isScheduleEntryType,
		isSingleRow: !!props.isSingleRow,
		isDragging: props.isDragging,
		backgroundImage: getBackgroundImage(
			isScheduleEntryType ? 1 : 0.1,
			props.colorHexes
		),
		hoverBackgroundImage: getBackgroundImage(
			isScheduleEntryType ? 0.9 : 0.2,
			props.colorHexes
		),
	};

	const classes = useStyles(styleProps);
	const style = { ...props.style };

	if (props.isCutOff.left) {
		style.borderTopLeftRadius = 0;
		style.borderBottomLeftRadius = 0;
		style.borderLeftStyle = "dashed";
	}

	if (props.isCutOff.right) {
		style.borderTopRightRadius = 0;
		style.borderBottomRightRadius = 0;
		style.borderRightStyle = "dashed";
	}

	const shouldHideTooltip = props.isDragging || props.isResizing;

	return (
		<Tooltip title={shouldHideTooltip ? "" : (props.tooltip || "")} placement="top">
			<div
				className={classes.dragElement}
				key={props.calendarEntry.id}
				onClick={props.onClick}
				ref={props.preview}
				style={style}
				role="button"
			>
				{props.children}
			</div>
		</Tooltip>
	);
};

export default CalendarEntryDraggableView;

function getBackgroundImage(opacity: number, colorHexes?: string[]) {
	if (colorHexes) {
		const lines = colorHexes.map((colorHex, index) => {
			const rgbColor = getColorRgbaFromHex(colorHex, opacity);

			return `${rgbColor} ${
				index * MERGED_BACKGROUND_LINE_WIDTH
			}px, ${rgbColor} ${(index + 1) * MERGED_BACKGROUND_LINE_WIDTH}px`;
		});

		return `repeating-linear-gradient(45deg, ${lines.join(", ")})`;
	}
}

const useStyles = makeStyles({
	dragElement: (props: StyleProps) => ({
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: props.backgroundImage ? "center" : "start",
		userSelect: "none",
		touchCallout: "none",
		position: "absolute",
		top: 0,
		left: 0,
		height:
			props.isScheduleEntryType && !props.isSingleRow
				? CALENDAR_ENTRY_TYPE_HEIGHT
				: CALENDAR_ENTRY_HEIGHT,
		boxSizing: "border-box",
		borderRadius: px.xs,
		border: props.isDragging
			? `1px dashed ${props.colorHex}`
			: `1px solid ${props.colorHex}`,
		color: getTextColorFromBackgroundColor(
			props.colorHex,
			props.isScheduleEntryType && !props.isDragging
		),
		fontSize: "10px",
		fontWeight: "bold",
		backgroundImage: props.backgroundImage,
		backgroundColor: props.backgroundImage
			? undefined
			: props.isDragging
			? getColorRgbaFromHex(props.colorHex, 0.15)
			: props.isScheduleEntryType
			? props.colorHex
			: getColorRgbaFromHex(props.colorHex, 0.1),
		"&:hover": {
			backgroundColor: props.hoverBackgroundImage
				? undefined
				: props.isDragging
				? getColorRgbaFromHex(props.colorHex, 0.15)
				: props.isScheduleEntryType
				? getColorRgbaFromHex(props.colorHex, 0.9)
				: getColorRgbaFromHex(props.colorHex, 0.2),
			backgroundImage: props.hoverBackgroundImage,
			// zoom in icon
			"& > svg": {
				display: "block",
			},
		},
	}),
});
